import React, { useState, useEffect, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { ControlPanel, ControlGroup, Loader } from '../components/Components';
import './gene.css';
import Tooltip from './Tooltip';

// Searchable Select Component
const SearchableSelect = React.memo(({ options = [], value, onChange, placeholder, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(option =>
            option.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.searchable-select')) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="searchable-select">
<div className="searchable-select-input" onClick={() => setIsOpen(true)}>
                <input
                    type="text"
                    value={isOpen ? searchTerm : value || ''}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={placeholder}
                    onFocus={() => setIsOpen(true)}
                    className="search-input"
                />
                <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>

            {isOpen && (
                <div className="searchable-select-dropdown">
                    <div className="dropdown-content">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    className={`dropdown-option ${option === value ? 'selected' : ''}`}
                                    onClick={() => handleSelect(option)}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-no-results">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

function MetabolicPage({ 
    selections, 
    handleSelection, 
    options, 
    data, 
    isTabLoading,
    diseaseColors 
}) {
    const shouldShowLoading = isTabLoading;

    // Clean plot configuration
    const plotConfig = {
        displayModeBar: false,
        responsive: true
    };

    const plotLayout = {
        font: { family: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 11 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(248, 250, 252, 0.5)',
        margin: { l: 50, r: 20, t: 0, b: 170 }, // Increased bottom margin for rotated labels
        showlegend: false
    };

    // Create box plot data with disease colors for subsystem level
    const createSubsystemPlotData = () => {
        const plotData = data.metabolicSubsystemData;
        if (!plotData || plotData.length === 0) return [];

        return Array.from(new Set(plotData.map(d => d.disease_name_level2) || [])).map(disease => ({
            y: plotData.filter(d => d.disease_name_level2 === disease).map(d => d.mean_activity),
            x: plotData.filter(d => d.disease_name_level2 === disease).map(d => d.disease_name_level2),
            type: 'box',
            name: disease,
            boxpoints: 'all',
            jitter: 0.3,
            pointpos: 0,
            fillcolor: 'rgba(0,0,0,0)',
            line: { color: 'rgba(0,0,0,0.3)', width: 1 },
            marker: {
                color: diseaseColors[disease] || '#cccccc',
                size: 4,
                opacity: 0.7
            },
            hovertemplate: `<b>${disease}</b><br>Mean Activity: %{y:.3f}<extra></extra>`
        }));
    };

    // Create box plot data with disease colors for reaction level
    const createReactionPlotData = () => {
        const plotData = data.metabolicReactionData;
        if (!plotData || plotData.length === 0) return [];

        return Array.from(new Set(plotData.map(d => d.disease_name_level2) || [])).map(disease => ({
            y: plotData.filter(d => d.disease_name_level2 === disease).map(d => d.activity),
            x: plotData.filter(d => d.disease_name_level2 === disease).map(d => d.disease_name_level2),
            type: 'box',
            name: disease,
            boxpoints: 'all',
            jitter: 0.3,
            pointpos: 0,
            fillcolor: 'rgba(0,0,0,0)',
            line: { color: 'rgba(0,0,0,0.3)', width: 1 },
            marker: {
                color: diseaseColors[disease] || '#cccccc',
                size: 4,
                opacity: 0.7
            },
            hovertemplate: `<b>${disease}</b><br>Activity: %{y:.3f}<extra></extra>`
        }));
    };

    // Check if reaction data is available
    const hasReactionData = data.metabolicReactionData && data.metabolicReactionData.length > 0;

    return (
        <div className="gene-page">
            {/* Sidebar */}
            <div className="gene-sidebar">
                <h1 className="gene-sidebar-title">Metabolic Signatures</h1>
                
                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Subsystem</p>
                    <SearchableSelect
                        options={options.subsystems || []}
                        value={selections.metabolicSubsystem}
                        onChange={(value) => handleSelection('metabolicSubsystem', value)}
                        placeholder="Search subsystems..."
                    />
                </div>

                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Reaction</p>
                    <SearchableSelect
                        options={options.metabolicReactions || []}
                        value={selections.metabolicReaction}
                        onChange={(value) => handleSelection('metabolicReaction', value)}
                        placeholder="Search reactions..."
                    />
                </div>
            </div>

            {/* Main Content */}
            <div className="gene-main-content">
                {/* Loading State */}
                {shouldShowLoading && (
                    <div className="gene-loading">
                        <div className="gene-loading-spinner"></div>
                        Loading metabolic analysis data...
                    </div>
                )}

                {/* Content */}
                {!shouldShowLoading && (
                    <div className="gene-content">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            
                            {/* Subsystem Level Analysis */}
                            <div className="gene-glass-card">
                                <div className="gene-card-title">
                                    Metabolic Features at Subsystem Level
                                    <span className="gene-tag">{selections.metabolicSubsystem}</span>
                                </div>
                                <p className="gene-card-subtitle">
                                    Mean metabolic activity patterns across diseases for the selected subsystem
                                </p>
                                
                                <div className="gene-plot-container" style={{ height: '400px', overflow: 'hidden' }}>
                                    <Plot 
                                        data={createSubsystemPlotData()}
                                        layout={{
                                            ...plotLayout,
                                            height: 400,
                                            xaxis: { 
                                                title: { text: 'Disease Type', font: { size: 14, color: '#374151' } },
                                                tickangle: -45,
                                                tickfont: { size: 11, color: '#6b7280' },
                                                showgrid: false,
                                                showline: true,
                                                linecolor: 'rgba(0,0,0,0.2)',
                                                linewidth: 1
                                            },
                                            yaxis: { 
                                                title: { text: 'Mean Activity', font: { size: 14, color: '#374151' } },
                                                tickfont: { size: 12, color: '#6b7280' },
                                                gridcolor: 'rgba(0,0,0,0.1)',
                                                showgrid: true
                                            }
                                        }}
                                        config={plotConfig}
                                        useResizeHandler={true}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* Reaction Level Analysis */}
                            <div className="gene-glass-card">
                                <div className="gene-card-title">
                                    Metabolic Features at Reaction Level
                                    <span className="gene-tag">{selections.metabolicReaction}</span>
                                </div>
                                <p className="gene-card-subtitle">
                                    Individual reaction activity patterns across diseases for the selected reaction
                                    {!hasReactionData && " • No data available for this reaction"}
                                </p>
                                
                                <div className="gene-plot-container" style={{ height: '400px', overflow: 'hidden' }}>
                                    {hasReactionData ? (
                                        <Plot 
                                            data={createReactionPlotData()}
                                            layout={{
                                                ...plotLayout,
                                                height: 400,
                                                xaxis: { 
                                                    title: { text: 'Disease Type', font: { size: 14, color: '#374151' } },
                                                    tickangle: -45,
                                                    tickfont: { size: 11, color: '#6b7280' },
                                                    showgrid: false,
                                                    showline: true,
                                                    linecolor: 'rgba(0,0,0,0.2)',
                                                    linewidth: 1
                                                },
                                                yaxis: { 
                                                    title: { text: 'Activity', font: { size: 14, color: '#374151' } },
                                                    tickfont: { size: 12, color: '#6b7280' },
                                                    gridcolor: 'rgba(0,0,0,0.1)',
                                                    showgrid: true
                                                }
                                            }}
                                            config={plotConfig}
                                            useResizeHandler={true}
                                            style={{ width: '100%', height: '100%' }}
                                        />
                                    ) : (
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            height: '100%',
                                            flexDirection: 'column',
                                            color: '#6b7280',
                                            gap: '10px'
                                        }}>
                                            <div style={{ fontSize: '18px' }}>📊</div>
                                            <div style={{ fontSize: '14px', textAlign: 'center' }}>
                                                <div>No reaction data available</div>
                                                <div style={{ fontSize: '12px', marginTop: '5px' }}>
                                                    Selected: {selections.metabolicReaction || 'None'}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                                    Available reactions: {options.metabolicReactions?.length || 0}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Cache Note */}
                                <div style={{ 
                                    marginTop: '10px', 
                                    padding: '8px 12px', 
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    color: '#374151'
                                }}>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MetabolicPage;
